import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  Grid,
  makeStyles,
  Modal,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import CloseIcon from "@mui/icons-material/Close";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import useLogs from "hooks/logs";
import Api from "lib/api/httpRequestHelper";
import { useEffect, useRef, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { IsMobile } from "utils/is-mobile";

const MAX_IMAGES = 4;
const MAX_IMAGE_SIZE = 1024 * 1024; // 1MB

const useStyles = makeStyles(() => ({
  th: {
    color: "#32325d",
    fontSize: ".75rem",
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: 1.5,
    marginBottom: ".5rem",
    textAlign: "center",
    paddingBottom: "15px",
  },
  td: {
    fontSize: ".75rem",
    fontFamily: "inherit",
    fontWeight: 500,
    textAlign: "center",
    padding: "10px 0px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  widthLarge: {
    width: "15vw",
    maxWidth: "15vw",
  },
  devolucao: {
    width: "5vw",
    maxWidth: "5vw",
  },
  hiddenInput: {
    display: "none",
  },
  imageContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "8px",
    marginBottom: "8px",
  },
  imageWrapper: {
    position: "relative",
    width: "20px",
    height: "20px",
  },
  imagePreview: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "4px",
    border: "1px solid #ddd",
  },
  removeImageButton: {
    position: "absolute",
    top: -14,
    right: -14,
    padding: "4px 7px",
  },
  imagesCell: {
    minWidth: "100px",
    padding: "16px 8px",
  },
}));

export default function ChooseInvoiceDialog({
  open,
  onClose,
  invoice,
  selectedProductsList,
  setSelectedProductsList,
}) {
  const [products, setProducts] = useState(
    invoice.items.map((product) => ({
      ...product,
      images: [],
    }))
  );

  const [reasonsList, setReasonsList] = useState([]);
  const [selectedReason, setSelectedReason] = useState();
  const [selectedReasonComplete, setSelectedReasonComplete] = useState();
  const [searchReason, setSearchReason] = useState(true);

  const [defectList, setDefectList] = useState([]);
  const [defectByReason, setDefectByReason] = useState([]);
  const [selectedDefect, setSelectedDefect] = useState();
  const [selectedDefectComplete, setSelectedDefectComplete] = useState();
  const [searchDefect, setSearchDefect] = useState(true);

  const [quantityError, setQuantityError] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [hasSelected, setHasSelected] = useState(false);

  const [showingPhoto, setShowingPhoto] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [checkbox, setCheckbox] = useState(false);

  const isMobile = IsMobile();

  const { setLocalStorageApiProblem } = useLogs();
  const snackbar = useSnackbar();
  const classes = useStyles();
  const fileInputRefs = useRef({});

  const handleChangeQuantity = (product, quantity) => {
    setQuantityError(false);
    setProducts(
      products.map((p) =>
        p.idProduto === product.idProduto ? { ...p, quantidade: +quantity } : p
      )
    );
    if (quantity > product.QtdFaturada - product.QuantidadeDevolvida) {
      setQuantityError(true);
    }
  };

  const handleChangeSelected = (codigo) => {
    const newProducts = products.map((p) =>
      p.idProduto === codigo ? { ...p, Selected: !p.Selected } : p
    );
    setProducts(newProducts);
    const hasSelected = newProducts.some((p) => p.Selected);
    setHasSelected(hasSelected);
  };

  const handleImageUpload = (event, productId) => {
    const files = Array.from(event.target.files);
    const currentProduct = products.find((p) => p.idProduto === productId);
    const currentImages = currentProduct?.images || [];

    if (currentImages.length + files.length > MAX_IMAGES) {
      snackbar.showMessage(
        `Máximo de ${MAX_IMAGES} imagens permitido por produto`,
        {
          severity: "error",
        }
      );
      return;
    }

    const validFiles = [];
    files.forEach((file) => {
      if (file.size > MAX_IMAGE_SIZE) {
        snackbar.showMessage(`Tamanho máximo de imagem permitido é 1MB`, {
          severity: "error",
        });
        return;
      }
      validFiles.push(file);
    });

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.idProduto === productId
              ? {
                  ...product,
                  images: [
                    ...(product.images || []),
                    {
                      base64: reader.result,
                      name: file.name,
                      extension: file.name.split(".").pop().toLowerCase(),
                    },
                  ],
                }
              : product
          )
        );

        // Atualiza a modal se estiver aberta para o mesmo produto
        if (showingPhoto && showingPhoto.productId === productId) {
          const updatedProduct = products.find(
            (p) => p.idProduto === productId
          );
          setShowingPhoto({
            ...showingPhoto,
            images: [
              ...(updatedProduct?.images || []),
              {
                base64: reader.result,
                name: file.name,
                extension: file.name.split(".").pop().toLowerCase(),
              },
            ],
          });
        }
      };
      reader.readAsDataURL(file);
    });

    // Limpa o input para permitir selecionar os mesmos arquivos novamente
    event.target.value = "";
  };

  const handleRemoveImage = (productId, imageIndex) => {
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.idProduto === productId
          ? {
              ...product,
              images: product.images.filter((_, index) => index !== imageIndex),
            }
          : product
      )
    );

    // Atualiza o índice da imagem atual se necessário
    if (showingPhoto && showingPhoto.productId === productId) {
      const updatedProduct = products.find((p) => p.idProduto === productId);
      const updatedImages = updatedProduct.images.filter(
        (_, index) => index !== imageIndex
      );

      if (updatedImages.length === 0) {
        setShowingPhoto(null);
      } else {
        const newIndex =
          imageIndex >= updatedImages.length
            ? updatedImages.length - 1
            : currentImageIndex;
        setCurrentImageIndex(newIndex);
        setShowingPhoto({
          ...showingPhoto,
          images: updatedImages,
        });
      }
    }
  };

  const handleOpenImageModal = (product) => {
    setShowingPhoto({
      productId: product.idProduto,
      productName: product.name,
      images: product.images || [],
    });
    setCurrentImageIndex(0);
  };

  const handleCloseModal = () => {
    setShowingPhoto(null);
    setCurrentImageIndex(0);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) =>
      Math.min(showingPhoto.images.length - 1, prev + 1)
    );
  };

  const handleAddImagesFromModal = (productId) => {
    if (fileInputRefs.current[productId]) {
      fileInputRefs.current[productId].click();
    }
  };

  const handleGetReasons = async () => {
    setSearchReason(true);
    await Api.get("list-reason", "trocasDevolucoes", "", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setReasonsList(Result.ListaMotivos);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "191",
          screen: "Motivos de Devolucoes",
          problem: `Erro na chamada do endpoint list-reasons - ${error}`,
        });
      })
      .finally(() => setSearchReason(false));
  };

  const handleGetDefects = async () => {
    setSearchDefect(true);
    await Api.get("list-defect", "trocasDevolucoes", "?page=0", true)
      .then(({ Success, Result }) => {
        if (Success) {
          setDefectList(Result.ListaDefeitos);
        }
      })
      .catch((error) => {
        snackbar.showMessage(error, {
          severity: "error",
        });
        setLocalStorageApiProblem({
          idMenuAccess: "191",
          screen: "Motivos de Defeitos",
          problem: `Erro na chamada do endpoint list-defect - ${error}`,
        });
      })
      .finally(() => setSearchDefect(false));
  };

  const handleSetReason = (reason) => {
    const newProducts = products.map((product) => ({
      ...product,
      idMotivo: reason.IdMotivo,
      motivo: reason.Motivo,
    }));
    setProducts(newProducts);
  };

  const handleSetDefect = (defect) => {
    const newProducts = products.map((product) => ({
      ...product,
      idDefeito: defect.IdDefeito,
      defeito: defect.Defeito,
    }));
    setProducts(newProducts);
  };

  const validateSelectedProducts = () => {
    const selectedProducts = products.filter((product) => product.Selected);
    const isValid = selectedProducts.every((product) => {
      return (
        product.idMotivo &&
        product.quantidade > 0 &&
        (defectByReason?.length === 0 || product.idDefeito)
      );
    });
    if (!isValid) {
      setIsValid(false);
    }
    setIsValid(true);
    return isValid;
  };

  const handleClose = () => {
    const newInvoice = {
      DataEmissaoNf: invoice.DataEmissaoNf,
      NumeroNf: invoice.NumeroNf,
      SerieNf: invoice.SerieNf,
      ValorTotal: invoice.ValorTotal,
      items: products.filter((product) => product.Selected),
    };
    setSelectedProductsList([...selectedProductsList, newInvoice]);
    onClose();
  };

  const handleSubmit = () => {
    const valid = validateSelectedProducts();
    if (valid) {
      handleClose();
    }
    setIsValid(false);
    return;
  };

  useEffect(() => {
    handleGetReasons();
    handleGetDefects();
  }, []);

  useEffect(() => {
    setSearchDefect(true);
    const defectByReason = defectList.filter(
      (defect) => defect.IdMotivo === selectedReason
    );
    if (defectByReason.length === 0) {
      const newProducts = products.map((product) => ({
        ...product,
        idDefeito: undefined,
        defeito: undefined,
      }));
      setProducts(newProducts);
    }
    setDefectByReason(defectByReason);
    setSearchDefect(false);
  }, [selectedReason]);

  useEffect(() => {
    if (reasonsList.length > 0 && selectedReason) {
      const matchingReason = reasonsList.find(
        (reason) => reason.IdMotivo === selectedReason
      );
      if (matchingReason) {
        setSelectedReasonComplete(matchingReason);
        handleSetReason(matchingReason);
      }
    }
  }, [reasonsList, selectedReason]);

  useEffect(() => {
    setSearchDefect(true);
    if (defectList.length > 0 && selectedDefect) {
      const matchingDefect = defectList.find(
        (defect) => defect.IdDefeito === selectedDefect
      );
      if (matchingDefect) {
        setSelectedDefectComplete(matchingDefect);
        handleSetDefect(matchingDefect);
      }
    }
    setSearchDefect(false);
  }, [defectList, selectedDefect]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      maxWidth={"xl"}
    >
      <PerfectScrollbar>
        <DialogTitle>
          <Typography variant="h3" component="h3">
            Escolha os Produtos para Vincular a Solicitação
          </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: "0" }}>
          <Grid
            container
            spacing={2}
            direction={isMobile ? "column" : "row"}
            style={{ margin: 0 }}
          >
            <Grid items xs={isMobile ? 12 : 3}>
              <Typography variant="h4" component="h3">
                Nota Fiscal:
                <span style={{ fontWeight: "400" }}> {invoice?.NumeroNf}</span>
              </Typography>
            </Grid>
            <Grid items xs={isMobile ? 12 : 3}>
              <Typography variant="h4" component="h3">
                Serie:
                <span style={{ fontWeight: "400" }}> {invoice?.SerieNf}</span>
              </Typography>
            </Grid>
            <Grid items xs={isMobile ? 12 : 6}>
              <Typography variant="h4" component="h3">
                Data de Emissão:
                <span style={{ fontWeight: "400" }}>
                  {" "}
                  {invoice?.DataEmissaoNf}
                </span>
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={isMobile ? 12 : 4}>
              <AutoCompleteField
                label="Motivo de Devolução"
                onChange={(value) => {
                  setSelectedReason(value.IdMotivo);
                  setSelectedReasonComplete(value);
                }}
                disabled={reasonsList?.length > 0 || defectList?.length > 0}
                loading={searchReason}
                value={selectedReasonComplete}
                options={reasonsList}
                optionLabelKey="Motivo"
                optionIdKey="IdMotivo"
              />
            </Grid>
            {(defectByReason?.length > 0 || searchDefect) && (
              <Grid item xs={isMobile ? 12 : 4}>
                <AutoCompleteField
                  label="Motivo de Defeito"
                  onChange={(value) => {
                    setSelectedDefect(value.IdDefeito);
                    setSelectedDefectComplete(value);
                  }}
                  options={defectByReason}
                  value={selectedDefectComplete}
                  loading={searchDefect}
                  disabled={
                    defectList?.length > 0 || defectByReason?.length > 0
                  }
                  optionLabelKey="Defeito"
                  optionIdKey="IdDefeito"
                />
              </Grid>
            )}
          </Grid>

          <Grid container>
            <Grid
              item
              xs={12}
              style={{
                border: "1px solid #ddd",
                borderRadius: "6px",
                padding: "15px",
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow style={{ borderBottom: "1px solid #ddd" }}>
                      <TableCell class={classes.th}>
                        <Checkbox
                          checked={checkbox}
                          onChange={() => {
                            setProducts(
                              products.map((product) => ({
                                ...product,
                                Selected: !checkbox,
                              }))
                            );
                            setCheckbox(!checkbox);
                          }}
                          name="ativo"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell class={classes.th}>Cód Pedido</TableCell>
                      <TableCell class={classes.th}>Cód Produto</TableCell>
                      <TableCell class={classes.th}>Descrição</TableCell>
                      <TableCell class={classes.th}>Qtd NF</TableCell>
                      <TableCell class={classes.th}>Qtd Devolvida</TableCell>
                      <TableCell class={classes.th}>Valor unitário</TableCell>
                      <TableCell class={classes.th}>Valor Total</TableCell>
                      <TableCell class={`${classes.th} ${classes.devolucao}`}>
                        Devolução
                      </TableCell>
                      <TableCell class={`${classes.th} ${classes.devolucao}`}>
                        Imagens
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => {
                      const productImages = product.images || [];
                      return (
                        <TableRow key={product.idProduto}>
                          <TableCell class={classes.td}>
                            <Checkbox
                              checked={product.Selected || false}
                              onChange={() => {
                                handleChangeSelected(product.idProduto);
                              }}
                              name="ativo"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.IdPedido}
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.idProduto}
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.name}
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.QtdFaturada}
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.QuantidadeDevolvida}
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.ValorUnitario}
                          </TableCell>
                          <TableCell class={classes.td}>
                            {product.ValorTotal}
                          </TableCell>
                          <TableCell
                            class={`${classes.td} ${classes.devolucao}`}
                          >
                            <OutlinedInput
                              fullWidth
                              type="text"
                              value={product.quantidade}
                              onChange={(e) =>
                                handleChangeQuantity(
                                  product,
                                  e.target.value.replace(/\D/g, "")
                                )
                              }
                              style={{ width: "80%" }}
                            />
                          </TableCell>
                          <TableCell
                            class={`${classes.td} ${classes.imagesCell}`}
                          >
                            <div style={{ textAlign: "center" }}>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(event) =>
                                  handleImageUpload(event, product.idProduto)
                                }
                                ref={(ref) =>
                                  (fileInputRefs.current[product.idProduto] =
                                    ref)
                                }
                                className={classes.hiddenInput}
                              />

                              {productImages.length === 0 ? (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() =>
                                    fileInputRefs.current[
                                      product.idProduto
                                    ]?.click()
                                  }
                                  style={{
                                    fontSize: "0.7rem",
                                    padding: "4px 8px",
                                    minWidth: "auto",
                                  }}
                                >
                                  Adicionar
                                </Button>
                              ) : (
                                <div
                                  className={classes.imageIcon}
                                  onClick={() => handleOpenImageModal(product)}
                                  title={`${productImages.length} imagem${
                                    productImages.length > 1 ? "s" : ""
                                  }`}
                                >
                                  <i className="fas fa-images" />
                                  <Typography
                                    variant="caption"
                                    style={{
                                      display: "block",
                                      fontSize: "0.6rem",
                                    }}
                                  >
                                    {productImages.length}
                                  </Typography>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          {quantityError && (
            <Grid>
              <Typography
                variant="h5"
                component="h5"
                style={{ color: "grey", marginTop: "1rem" }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{
                    fontSize: "1rem",
                    marginRight: "0.5rem",
                  }}
                />{" "}
                Quantidade de devolução maior que a quantidade disponível
              </Typography>
            </Grid>
          )}
          {!isValid && (
            <Grid>
              <Typography
                variant="h5"
                component="h5"
                style={{ color: "grey", marginTop: "1rem" }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  aria-hidden="true"
                  style={{
                    fontSize: "1rem",
                    marginRight: "0.5rem",
                  }}
                />{" "}
                É necessário informar o motivo e o defeito
              </Typography>
            </Grid>
          )}
        </DialogContent>
        <DialogActions style={{ justifyContent: "center" }}>
          <Grid item xs={3}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                quantityError ||
                !selectedReason ||
                (defectByReason?.length > 0 && !selectedDefect) ||
                !hasSelected
              }
              style={{ width: "100%" }}
            >
              Adicionar
            </Button>
          </Grid>
        </DialogActions>
      </PerfectScrollbar>

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={Boolean(showingPhoto)}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Fade in={Boolean(showingPhoto)}>
          <div
            className={classes.paper}
            style={{
              backgroundColor: "#FFF",
              padding: "20px",
              borderRadius: 8,
              maxWidth: "90vw",
              maxHeight: "90vh",
              overflow: "auto",
            }}
          >
            <Box
              display="flex"
              justifyContent={isMobile ? "space-around" : "space-between"}
              marginBottom="1rem"
            >
              <Typography variant="h4" style={{ margin: 0 }}>
                {showingPhoto?.productName}
              </Typography>
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={handleCloseModal}
              >
                <Typography variant="caption">Fechar</Typography>
                <CloseIcon
                  onClick={handleCloseModal}
                  style={{ marginLeft: "0.5rem" }}
                />
              </Box>
            </Box>

            {showingPhoto &&
              showingPhoto.images &&
              showingPhoto.images.length > 0 && (
                <>
                  <Box
                    display="flex"
                    justifyContent="center"
                    marginBottom="1rem"
                  >
                    <img
                      alt={`Imagem ${currentImageIndex + 1}`}
                      src={
                        showingPhoto.images[currentImageIndex]?.base64 ||
                        showingPhoto.images[currentImageIndex]?.Url
                      }
                      style={{
                        width: "50vw",
                        maxHeight: "500px",
                        objectFit: "contain",
                      }}
                    />
                  </Box>

                  {showingPhoto.images.length > 1 && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      marginTop="1rem"
                    >
                      <Button
                        variant="outlined"
                        onClick={handlePrevImage}
                        disabled={currentImageIndex === 0}
                      >
                        Anterior
                      </Button>

                      <Typography variant="body2">
                        {currentImageIndex + 1} de {showingPhoto.images.length}
                      </Typography>

                      <Button
                        variant="outlined"
                        onClick={handleNextImage}
                        disabled={
                          currentImageIndex === showingPhoto.images.length - 1
                        }
                      >
                        Próxima
                      </Button>
                    </Box>
                  )}

                  <Box display="flex" justifyContent="center" marginTop="1rem">
                    <Typography variant="caption" color="textSecondary">
                      {showingPhoto.images[currentImageIndex]?.name}
                    </Typography>
                  </Box>

                  {/* Botões de Ação */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    gap="20px"
                    marginTop="1rem"
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        handleRemoveImage(
                          showingPhoto.productId,
                          currentImageIndex
                        )
                      }
                    >
                      Remover Imagem
                    </Button>

                    {showingPhoto.images.length < MAX_IMAGES && (
                      <Button
                        variant="contained"
                        onClick={() =>
                          handleAddImagesFromModal(showingPhoto.productId)
                        }
                      >
                        Adicionar Imagens
                      </Button>
                    )}
                  </Box>
                </>
              )}

            {/* Caso não tenha imagens */}
            {showingPhoto &&
              (!showingPhoto.images || showingPhoto.images.length === 0) && (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap="1rem"
                >
                  <Typography variant="body1" color="textSecondary">
                    Nenhuma imagem adicionada
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() =>
                      handleAddImagesFromModal(showingPhoto.productId)
                    }
                  >
                    Adicionar Imagens
                  </Button>
                </Box>
              )}
          </div>
        </Fade>
      </Modal>
    </Dialog>
  );
}
